import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { LayoutDashboard, Package, ShoppingCart, LogOut, Store, Tags, Ticket, Users, Menu, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const AdminLayout = () => {
  const { loading, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!loading && !isAdmin) navigate("/admin/login");
  }, [loading, isAdmin, navigate]);

  // Səhifə dəyişəndə mobil menyu bağlanır.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Menyu açıqkən arxa fon sürüşməsin.
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  if (loading || !isAdmin) {
    return <div className="min-h-screen flex items-center justify-center text-brand-muted">Yüklənir...</div>;
  }

  // Toxunma sahəsi ən azı 44px — telefonda barmaqla rahat basılsın.
  const link = ({ isActive }) =>
    `flex items-center gap-3 px-5 py-3.5 text-sm rounded-md transition-colors ${
      isActive ? "bg-brand-ink text-white" : "text-brand-sub hover:bg-brand-bg2"
    }`;

  const nav = (
    <>
      <nav className="flex-1 p-4 space-y-1">
        <NavLink to="/admin" end className={link} data-testid="admin-nav-dashboard">
          <LayoutDashboard size={18} strokeWidth={1.5} /> Panel
        </NavLink>
        <NavLink to="/admin/products" className={link} data-testid="admin-nav-products">
          <Package size={18} strokeWidth={1.5} /> Məhsullar
        </NavLink>
        <NavLink to="/admin/orders" className={link} data-testid="admin-nav-orders">
          <ShoppingCart size={18} strokeWidth={1.5} /> Sifarişlər
        </NavLink>
        <NavLink to="/admin/categories" className={link} data-testid="admin-nav-categories">
          <Tags size={18} strokeWidth={1.5} /> Kateqoriyalar
        </NavLink>
        <NavLink to="/admin/discounts" className={link} data-testid="admin-nav-discounts">
          <Ticket size={18} strokeWidth={1.5} /> Endirim kartları
        </NavLink>
        <NavLink to="/admin/customers" className={link} data-testid="admin-nav-customers">
          <Users size={18} strokeWidth={1.5} /> Müştərilər
        </NavLink>
      </nav>
      <div className="p-4 border-t border-brand-line space-y-1">
        <Link to="/" className="flex items-center gap-3 px-5 py-3.5 text-sm text-brand-sub hover:bg-brand-bg2 rounded-md">
          <Store size={18} strokeWidth={1.5} /> Mağaza
        </Link>
        <button
          onClick={() => { logout(); navigate("/admin/login"); }}
          data-testid="admin-logout"
          className="w-full flex items-center gap-3 px-5 py-3.5 text-sm text-brand-sub hover:bg-brand-bg2 rounded-md"
        >
          <LogOut size={18} strokeWidth={1.5} /> Çıxış
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-brand-bg lg:flex">
      {/* Mobil üst zolaq */}
      <header className="lg:hidden sticky top-0 z-40 flex items-center justify-between border-b border-brand-line bg-white px-4 h-16">
        <button
          onClick={() => setMenuOpen(true)}
          aria-label="Menyunu aç"
          data-testid="admin-menu-toggle"
          className="p-2 -ml-2 text-brand-ink"
        >
          <Menu size={24} strokeWidth={1.5} />
        </button>
        <span className="font-serif text-xl tracking-[0.18em]">SPARKLE</span>
        <Link to="/" aria-label="Mağaza" className="p-2 -mr-2 text-brand-sub">
          <Store size={20} strokeWidth={1.5} />
        </Link>
      </header>

      {/* Mobil menyu — yandan açılan panel */}
      {menuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="admin-mobile-menu">
          <div
            className="absolute inset-0 bg-brand-ink/40"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />
          <aside className="relative w-72 max-w-[85vw] bg-white h-full flex flex-col shadow-xl">
            <div className="p-5 border-b border-brand-line flex items-start justify-between">
              <div>
                <span className="font-serif text-2xl tracking-[0.2em]">SPARKLE</span>
                <p className="overline mt-1">Admin</p>
              </div>
              <button
                onClick={() => setMenuOpen(false)}
                aria-label="Menyunu bağla"
                className="p-2 -mr-2 -mt-1 text-brand-sub"
              >
                <X size={22} strokeWidth={1.5} />
              </button>
            </div>
            {nav}
          </aside>
        </div>
      )}

      {/* Masaüstü yan menyu */}
      <aside className="hidden lg:flex w-64 border-r border-brand-line bg-white fixed h-screen flex-col">
        <div className="p-6 border-b border-brand-line">
          <span className="font-serif text-2xl tracking-[0.2em]">SPARKLE</span>
          <p className="overline mt-1">Admin</p>
        </div>
        {nav}
      </aside>

      {/* min-w-0 vacibdir: cədvəllər üfüqi sürüşə bilsin, səhifəni genişlətməsin */}
      <main className="flex-1 lg:ml-64 p-4 sm:p-6 lg:p-10 min-w-0">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
